import { createSlice } from '@reduxjs/toolkit';
import { sendAnalyticsEvents } from '../analytics/common';
import { EVENTS } from '../analytics/events';
import {
  getPollingID,
  sendStripeReq,
  subscribeToPriceChange,
} from '../utils/checkoutFetch';
import { calcPercent } from '../utils';
import { setEventData, setEventDataList } from './events';
import { setCheckoutLoader } from './loader';
import { setDeeplink } from './signup';
// import { setError } from './error';

export const checkoutSlice = createSlice({
  name: 'checkout',
  initialState: {
    cardholderName: '',
    token: null,
    isSuccess: null,
    isFormValid: null,
    id: null,
    regularPrice: null,
    stripeNonce: '',
    braintreeNonce: '',
    upsellType: 'old',
    upsellData: null,
    showPaymentError: false,
    errorText: '',
    isCardFormShown: false,
    showSuccessPopup: false,
  },
  reducers: {
    setCardholderName: (state, action) => {
      state.cardholderName = action.payload;
    },
    setRequestResult: (state, action) => {
      const { isSuccess, id, finalPrice, regularPrice, upsell_type } =
        action.payload;
      const upsellType = ['old', 'new'];

      state.isSuccess = isSuccess;
      state.id = id;
      state.finalPrice = finalPrice;
      state.regularPrice = regularPrice;
      state.upsellType = upsellType[upsell_type || 0];
    },
    setUpsellData: (state, action) => {
      state.upsellData = {
        success: action.payload?.isSuccess,
        finalPrice: action.payload?.finalPrice,
        regularPrice: action.payload?.regularPrice,
      };
    },
    setDiscountPercentage: (state, action) => {
      const { finalPrice, regularPrice } = action.payload;
      state.discountPercentage = calcPercent(
        regularPrice - finalPrice,
        regularPrice
      );
    },
    setFormValidity: (state, action) => {
      state.isFormValid = action.payload;
    },
    setStripeNonce: (state, action) => {
      state.stripeNonce = action.payload;
    },
    setBraintreeNonce: (state, action) => {
      state.braintreeNonce = action.payload;
    },
    setShowPaymentError: (state, action) => {
      const { show, text } = action.payload;
      state.showPaymentError = show;
      state.errorText = text;
    },
    setIfCardFormShown: (state, action) => {
      state.isCardFormShown = action.payload;
    },
    setShowSuccessPopup: (state, action) => {
      state.showSuccessPopup = action.payload;
    },
  },
});

export const {
  setCardholderName,
  setStripeNonce,
  setBraintreeNonce,
  setRequestResult,
  setDiscountPercentage,
  setFormValidity,
  setUpsellData,
  setShowPaymentError,
  setIfCardFormShown,
  setShowSuccessPopup,
} = checkoutSlice.actions;

const sendServerReq = async (
  stripe,
  paymentMethodObj,
  plan,
  token,
  analyticsParams,
  eventsData,
  onError,
  onSuccess,
  payment_intent_id = '',
  coupon = '',
  email,
  siteUpdates
) => {
  const { stripeNonce, methodName } = paymentMethodObj;
  const pollingID = await getPollingID(
    'stripe',
    stripeNonce,
    plan,
    token,
    analyticsParams,
    eventsData,
    onError,
    payment_intent_id,
    coupon,
    email,
    siteUpdates
  );
  const purchaseRequest = await subscribeToPriceChange(pollingID);
  const { status, errors, data } = purchaseRequest || {
    errors: { messages: 'Unknown error. Please try again' },
  };

  if (status) {
    if (status === 'requires_action' && data && data.payment_intent_secret) {
      const cardPaymentSecret = data.payment_intent_secret;
      const cardPaymentData = methodName
        ? { payment_method: stripeNonce }
        : { setup_future_usage: 'off_session' };
      const cardPaymentOptions = methodName ? { handleActions: false } : {};

      const { error: errorAction, paymentIntent } =
        await stripe.confirmCardPayment(
          cardPaymentSecret,
          cardPaymentData,
          cardPaymentOptions
        );

      if (errorAction) {
        if (methodName) {
          onError(errorAction.message, data);
        } else {
          await sendServerReq(
            stripe,
            paymentMethodObj,
            plan,
            token,
            analyticsParams,
            eventsData,
            onError,
            onSuccess,
            data.payment_intent_id,
            coupon,
            email,
            siteUpdates
          );
        }
        return;
      }
      if (methodName) {
        if (paymentIntent.status === 'requires_action') {
          const { error } = await stripe.confirmCardPayment(
            data.payment_intent_secret
          );
          if (error) {
            onError(error, data);
            return;
          }
        }

        onSuccess();
      }

      await sendServerReq(
        stripe,
        paymentMethodObj,
        plan,
        token,
        analyticsParams,
        eventsData,
        onError,
        onSuccess,
        paymentIntent.id,
        coupon,
        email,
        siteUpdates
      );
    } else {
      const {
        subscription_id,
        price_with_discount,
        regular_price,
        upsell_type,
        deeplink_token,
        event_id,
      } = purchaseRequest;

      return onSuccess(
        subscription_id,
        event_id,
        price_with_discount,
        regular_price,
        upsell_type,
        deeplink_token
      );
    }
  }

  if (errors) {
    if (status === 'requires_action') return;

    onError(errors.messages || 'An error has occurred', data);
  }
};

export const sendMainPurchaseRequest =
  (
    paymentMethodObj = null,
    stripe,
    card,
    name,
    plan,
    token,
    analyticsParams,
    eventsData,
    successPayment,
    failedPayment,
    coupon,
    email,
    siteUpdates
  ) =>
  async (dispatch) => {
    const onError = (data, errorData) => {
      if (failedPayment) failedPayment();

      let txt = data;
      if (typeof txt !== 'string') txt = data?.message;
      if (typeof txt !== 'string') {
        txt = 'Payment error';
      }

      dispatch(
        setEventData({
          id: 'declineCode',
          data: errorData?.decline_code || errorData?.processor_response,
        })
      );
      sendAnalyticsEvents(EVENTS.onboardingPaymentDecline, {
        ...eventsData,
        declineCode: errorData?.decline_code || errorData?.processor_response,
      });
      sendAnalyticsEvents(EVENTS.PAYMENT_DECLINE, {
        ...eventsData,
        declineCode: errorData?.decline_code || errorData?.processor_response,
      });
      dispatch(setShowPaymentError({ show: true, text: txt }));
      // dispatch(setError({show: true, text: txt, type: 'payment'}));
    };
    const onSuccess = (
      id,
      event_id,
      finalPrice,
      regularPrice,
      upsell_type,
      deeplink_token
    ) => {
      if (successPayment) successPayment(id, event_id);
      if (finalPrice) {
        dispatch(setDiscountPercentage({ finalPrice, regularPrice }));
      }

      dispatch(
        setRequestResult({
          isSuccess: true,
          id,
          event_id,
          finalPrice,
          regularPrice,
          upsellType: upsell_type,
        })
      );
      dispatch(setDeeplink(deeplink_token));
      sendAnalyticsEvents(EVENTS.subscribed, eventsData);
      sendAnalyticsEvents(EVENTS.SUBSCRIBED, eventsData);
    };

    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    sendAnalyticsEvents(EVENTS.paymentAnimationScreen, eventsData);
    sendAnalyticsEvents(EVENTS.PAYMENT_ANIMATION, eventsData);

    try {
      if (paymentMethodObj) {
        dispatch(setStripeNonce(paymentMethodObj.stripeNonce));
        await sendServerReq(
          stripe,
          paymentMethodObj,
          plan,
          token,
          analyticsParams,
          eventsData,
          onError,
          onSuccess,
          '',
          coupon,
          email,
          siteUpdates
        );
      } else {
        const { paymentMethod, error } = await sendStripeReq(
          stripe,
          card,
          name
        );
        const stripeNonce = paymentMethod?.id || error?.code;

        if (error) {
          console.error(error.message);
          dispatch(setShowPaymentError({ show: true, text: error.message }));
        } else {
          dispatch(setStripeNonce(stripeNonce));
          await sendServerReq(
            stripe,
            { stripeNonce },
            plan,
            token,
            analyticsParams,
            eventsData,
            onError,
            onSuccess,
            '',
            coupon,
            email,
            siteUpdates
          );
        }
      }
    } finally {
      dispatch(setCheckoutLoader({ show: false, type: '' }));
    }
  };

export const sendUpsellPurchaseRequest =
  (
    stripeNonce,
    plan,
    token,
    analyticsParams,
    eventsData,
    onSuccessEvents,
    onErrorEvents
  ) =>
  async (dispatch) => {
    function onError() {
      // console.log(...arguments);
      if (onErrorEvents) onErrorEvents(...arguments);
    }
    function onSuccess() {
      if (onSuccessEvents) onSuccessEvents(...arguments);
      // console.log(arguments);
    }

    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));

    try {
      await sendServerReq(
        null,
        { stripeNonce },
        plan,
        token,
        analyticsParams,
        eventsData,
        onError,
        onSuccess,
        '',
        ''
      );
    } finally {
      dispatch(setCheckoutLoader({ show: false, type: '' }));
    }
  };

export const braintreePurchase =
  (
    nonce,
    binData,
    threeDSInstance,
    plan,
    token,
    analyticsParams,
    eventsData,
    successPayment,
    coupon,
    email,
    siteUpdates
  ) =>
  async (dispatch) => {
    dispatch(setBraintreeNonce(nonce));

    const onError = (data, errorData) => {
      let txt = data;
      if (typeof txt !== 'string') txt = data.message;
      if (typeof txt !== 'string') {
        txt = 'Payment error';
      }

      dispatch(
        setEventData({
          id: 'decline_code',
          data: errorData?.decline_code || errorData?.processor_response,
        })
      );
      sendAnalyticsEvents(EVENTS.onboardingPaymentDecline, {
        ...eventsData,
        declineCode: errorData?.decline_code || errorData?.processor_response,
      });
      sendAnalyticsEvents(EVENTS.PAYMENT_DECLINE, {
        ...eventsData,
        declineCode: errorData?.decline_code || errorData?.processor_response,
      });
      dispatch(setShowPaymentError({ show: true, text: txt }));
      dispatch(setCheckoutLoader({ show: false, type: '' }));
    };
    const onSuccess = ({
      customer_id,
      subscription_id,
      deeplink_token,
      event_id,
    }) => {
      if (successPayment) successPayment(subscription_id, event_id);
      const localData = {
        customer_id,
        subscription_id,
        plan_id: plan,
      };
      dispatch(setRequestResult({ isSuccess: true, id: subscription_id }));
      dispatch(setEventDataList(localData));
      dispatch(setDeeplink(deeplink_token));
      sendAnalyticsEvents(EVENTS.subscribed, { ...eventsData, ...localData });
      sendAnalyticsEvents(EVENTS.SUBSCRIBED, {
        ...eventsData,
        ...localData,
      });
      dispatch(setCheckoutLoader({ show: false, type: '' }));
    };

    const pollingID = await getPollingID(
      'braintree',
      nonce,
      plan,
      token,
      analyticsParams,
      eventsData,
      onError,
      '',
      coupon,
      email,
      siteUpdates
    );
    const purchaseRequest = await subscribeToPriceChange(pollingID);
    const { errors, ...data } = purchaseRequest || {
      errors: { messages: 'Unknown error. Please try again' },
    };

    try {
      if (errors) {
        if (errors?.messages === 'User action required') {
          const { payment_method_nonce, validation_amount } = data;
          dispatch(setBraintreeNonce(payment_method_nonce));

          threeDSInstance.verifyCard(
            {
              nonce: payment_method_nonce,
              bin: binData,
              amount: validation_amount,
            },
            (verifyError, payload) => {
              if (verifyError) {
                console.error('verify error: ', verifyError);
                onError(verifyError, data);
                return;
              }
              // console.log('verify: ', payload);

              dispatch(
                braintreePurchase(
                  payload.nonce,
                  binData,
                  threeDSInstance,
                  plan,
                  token,
                  analyticsParams,
                  eventsData,
                  successPayment,
                  coupon,
                  email,
                  siteUpdates
                )
              );
            }
          );
          return;
        }

        onError(errors?.messages || 'An error has occurred', data);
        return;
      }
      onSuccess(data);
    } catch (err) {
      onError(err);
    }
  };

export const sendUpsellBraintreePurchase =
  (
    braintreeNonce,
    plan,
    token,
    analyticsParams,
    eventsData,
    onSuccessEvents,
    onErrorEvents
  ) =>
  async (dispatch) => {
    dispatch(setCheckoutLoader({ show: true, type: 'checkout' }));
    function onError() {
      if (onErrorEvents) onErrorEvents(...arguments);
    }
    function onSuccess({ subscription_id, event_id }) {
      onSuccessEvents?.(subscription_id, event_id);
    }

    try {
      const pollingID = await getPollingID(
        'braintree',
        braintreeNonce,
        plan,
        token,
        analyticsParams,
        eventsData,
        onError,
        '',
        '',
        ''
      );
      const purchaseRequest = await subscribeToPriceChange(pollingID);
      const { errors, ...data } = purchaseRequest || {
        errors: { messages: 'Unknown error. Please try again' },
      };

      if (errors) {
        console.error(errors);
        onError();
      } else {
        onSuccess(data);
      }
    } catch (err) {
      console.error(err);
      onError();
    } finally {
      dispatch(setCheckoutLoader({ show: false, type: '' }));
    }
  };

export const selectSuccess = (state) => state.checkout.isSuccess;
export const selectName = (state) => state.checkout.cardholderName;
export const selectFormValidity = (state) => state.checkout.isFormValid;
export const selectID = (state) => state.checkout.id;
export const selectStripeNonce = (state) => state.checkout.stripeNonce;
export const selectBraintreeNonce = (state) => state.checkout.braintreeNonce;
export const selectUpsellData = (state) => state.checkout.upsellData;
export const selectUpsellType = (state) => state.checkout.upsellType;
export const selectShowPaymentError = (state) =>
  state.checkout.showPaymentError;
export const selectErrorText = (state) => state.checkout.errorText;
export const selectIfCardFormShown = (state) => state.checkout.isCardFormShown;
export const selectShowSuccessPopup = (state) =>
  state.checkout.showSuccessPopup;

export default checkoutSlice.reducer;
